import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, doc, updateDoc, addDoc, writeBatch, onSnapshot, serverTimestamp, arrayUnion } from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { useUser } from "../shared/UserContext";
import { gradeFields, studentGrade } from "../shared/gradeModel";
import { toCsv, downloadCsv, slugify } from "../shared/csv";
import { useNavigate } from "react-router-dom";
import BulkImport from "./BulkImport";
import AddUserModal from "./AddUserModal";
import MobileTableCards from "../shared/MobileTableCards";

const ROLES = ["teacher", "specialist", "admin", "security"];

const ROLE_LABELS = {
  teacher:    "Teacher",
  specialist: "Specialist",
  admin:      "Admin",
  security:   "Security",
};

const SchoolAdmin = () => {
  const { schoolId, uid, role, loading, schoolYearEnd } = useUser();
  const navigate = useNavigate();
  const [users, setUsers]     = useState([]);
  const [classCounts, setClassCounts] = useState({}); // teacherId -> { active, archived }
  const [summary, setSummary] = useState({ students: 0, classes: 0, out: 0 });
  const [saving, setSaving]   = useState({});
  const [status, setStatus]   = useState({});
  const [fetching, setFetching] = useState(true);

  // Remove-from-school (type-to-confirm)
  const [removeTarget, setRemoveTarget] = useState(null); // the user doc being removed
  const [confirmText, setConfirmText]   = useState("");
  const [removing, setRemoving]         = useState(false);
  const [removeError, setRemoveError]   = useState(null);

  // Add-a-user modal
  const [showAddUser, setShowAddUser] = useState(false);
  const schoolName = users.find(u => u.id === uid)?.schoolName || "";

  // Pending join requests (admin-approved join)
  const [joinReqs, setJoinReqs] = useState([]);
  useEffect(() => {
    if (!schoolId || role !== "admin") return;
    // Single-field query (no composite index); filter to pending client-side.
    return onSnapshot(
      query(collection(firestore, "joinRequests"), where("schoolId", "==", schoolId)),
      snap => setJoinReqs(
        snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(r => r.status === "pending")
      ),
      err => console.error("join requests listen error:", err),
    );
  }, [schoolId, role]);

  const decideJoin = async (reqId, status) => {
    try {
      await updateDoc(doc(firestore, "joinRequests", reqId), {
        status,
        decidedBy: uid,
        decidedAt: serverTimestamp(),
      });
      // Approved requesters are auto-moved by their own client (JoinRequestWatcher);
      // either way it drops out of the pending list.
    } catch (err) {
      console.error("Error deciding join request:", err);
    }
  };

  // Active classes in the school, for matching a Homeroom/Class column on import.
  const [schoolClasses, setSchoolClasses] = useState([]); // [{ id, className }]

  // Access guard — admin only
  useEffect(() => {
    if (!loading && role !== "admin") navigate("/dashboard");
  }, [role, loading, navigate]);

  // Live roster: re-renders the moment a newly-approved member's user doc flips to
  // this schoolId (JoinRequestWatcher moves them on their end) — no manual refresh.
  // Merges by id so an in-progress role edit (pendingRole) isn't clobbered when the
  // snapshot fires.
  useEffect(() => {
    if (!schoolId) return;
    return onSnapshot(
      query(collection(firestore, "users"), where("schoolId", "==", schoolId)),
      snap => setUsers(prev => {
        const prevById = Object.fromEntries(prev.map(u => [u.id, u]));
        return snap.docs.map(d => {
          const data = d.data();
          const existing = prevById[d.id];
          return {
            id: d.id,
            ...data,
            // Keep a pending (unsaved) role choice; otherwise track the saved role.
            pendingRole: existing ? existing.pendingRole : (data.role || "teacher"),
          };
        });
      }),
      err => console.error("users listen error:", err),
    );
  }, [schoolId]);

  useEffect(() => {
    if (!schoolId) return;
    const fetchSchool = async () => {
      // Same-school reads the rules allow for an admin, for the at-a-glance cards +
      // per-teacher class counts: classes, students, active sign-outs. (The roster
      // itself is a live snapshot above; per-teacher sign-out history is owner-only,
      // so platform-wide signout totals live in the super-admin console.)
      const [classSnap, studentSnap, signoutSnap] = await Promise.all([
        getDocs(query(collection(firestore, "classes"),   where("schoolId", "==", schoolId))),
        getDocs(query(collection(firestore, "students"),  where("schoolId", "==", schoolId))),
        getDocs(query(collection(firestore, "signouts"),  where("schoolId", "==", schoolId))),
      ]);

      const counts = {};
      classSnap.docs.forEach(d => {
        const c = d.data();
        const t = c.teacherId;
        if (!t) return;
        if (!counts[t]) counts[t] = { active: 0, archived: 0 };
        if (c.isArchived) counts[t].archived += 1;
        else counts[t].active += 1;
      });
      setClassCounts(counts);

      // Active classes for the directory importer's Homeroom/Class matching.
      setSchoolClasses(
        classSnap.docs
          .filter(d => !d.data().isArchived)
          .map(d => ({ id: d.id, className: d.data().className || "" }))
      );

      setSummary({
        students: studentSnap.size,
        classes: classSnap.docs.filter(d => !d.data().isArchived).length,
        out: signoutSnap.size, // signouts holds only active records
      });
      setFetching(false);
    };
    fetchSchool();
  }, [schoolId]);

  // Download the school's roster as CSV (backs the DPA "return your data on request"
  // promise). Roster is fully readable to an admin (schoolId-scoped students); attendance
  // and completed sign-out history aren't school-readable yet (per-teacher, owner-only) —
  // those wait on the planned school-readable signoutLog. Re-fetches so the file is fresh.
  const [exporting, setExporting] = useState(false);
  const handleExportRoster = async () => {
    if (!schoolId) return;
    setExporting(true);
    try {
      const [studentSnap, classSnap] = await Promise.all([
        getDocs(query(collection(firestore, "students"), where("schoolId", "==", schoolId))),
        getDocs(query(collection(firestore, "classes"),  where("schoolId", "==", schoolId))),
      ]);
      const classNameById = {};
      classSnap.docs.forEach(d => { classNameById[d.id] = d.data().className || ""; });

      const rows = studentSnap.docs
        .map(d => {
          const s = d.data();
          const classNames = (s.enrolledClasses || [])
            .map(cid => classNameById[cid])
            .filter(Boolean);
          return {
            firstName: s.firstName || "",
            lastName: s.lastName || "",
            studentId: s.studentId || "",
            grade: studentGrade(s, schoolYearEnd) || "",
            homeroom: s.homeroomClassId ? (classNameById[s.homeroomClassId] || "") : "",
            classes: classNames.join("; "),
            directory: s.shared === true ? "Yes" : "No",
          };
        })
        .sort((a, b) =>
          a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName)
        );

      const csv = toCsv(rows, [
        { header: "First Name",   value: "firstName" },
        { header: "Last Name",    value: "lastName" },
        { header: "Student ID",   value: "studentId" },
        { header: "Grade",        value: "grade" },
        { header: "Homeroom",     value: "homeroom" },
        { header: "Classes",      value: "classes" },
        { header: "In Directory", value: "directory" },
      ]);
      const date = new Date().toISOString().slice(0, 10);
      downloadCsv(`${slugify(schoolName)}-roster-${date}.csv`, csv);
    } catch (err) {
      console.error("Roster export failed:", err);
      window.alert("Couldn't export the roster. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleRoleChange = (userId, newRole) => {
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, pendingRole: newRole } : u));
  };

  const saveRole = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setSaving(s => ({ ...s, [userId]: true }));
    try {
      await updateDoc(doc(firestore, "users", userId), { role: user.pendingRole });
      setStatus(s => ({ ...s, [userId]: "saved" }));
      setTimeout(() => setStatus(s => ({ ...s, [userId]: null })), 2000);
    } catch (err) {
      console.error("Error updating role:", err);
      setStatus(s => ({ ...s, [userId]: "error" }));
    } finally {
      setSaving(s => ({ ...s, [userId]: false }));
    }
  };

  // The string the admin must type to confirm a removal (their name, else "REMOVE").
  const removeLabel = (u) =>
    u ? (u.displayName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "REMOVE") : "";

  // Non-destructive removal: move the member + their classes + their PRIVATE roster
  // students to a fresh provisional "school of one". Shared directory students stay.
  const handleRemoveFromSchool = async () => {
    const target = removeTarget;
    if (!target || !schoolId) return;
    setRemoving(true);
    setRemoveError(null);
    try {
      // Find the member's own classes + private students FIRST (reads can't go in a
      // batch). Both queries filter on schoolId == current school, so rules authorize them.
      const [classSnap, studentSnap] = await Promise.all([
        getDocs(query(collection(firestore, "classes"),  where("schoolId", "==", schoolId))),
        getDocs(query(collection(firestore, "students"), where("schoolId", "==", schoolId))),
      ]);
      const myClasses  = classSnap.docs.filter(d => d.data().teacherId === target.id);
      const myStudents = studentSnap.docs.filter(d => d.data().addedBy === target.id && d.data().shared !== true);

      // One atomic batch: create the fresh school-of-one and re-stamp the member's
      // classes, private students, and user doc. All-or-nothing — no half-moves.
      const newSchoolName = `${removeLabel(target)}'s Class`;
      const batch = writeBatch(firestore);
      const newSchoolRef = doc(collection(firestore, "schools"));
      batch.set(newSchoolRef, {
        schoolName: newSchoolName,
        provisional: true,
        createdBy: uid,           // rules require createdBy == caller; member is in teachers[]
        teachers: [target.id],
      });
      myClasses.forEach(d  => batch.update(doc(firestore, "classes",  d.id), { schoolId: newSchoolRef.id }));
      myStudents.forEach(d => batch.update(doc(firestore, "students", d.id), { schoolId: newSchoolRef.id }));
      // Move schoolId AND its denormalized schoolName together (keeps the user doc /
      // super-admin console from showing a stale old-school name).
      batch.update(doc(firestore, "users", target.id), { schoolId: newSchoolRef.id, schoolName: newSchoolName });
      await batch.commit();

      // Drop them from the on-screen roster.
      setUsers(prev => prev.filter(u => u.id !== target.id));
      setRemoveTarget(null);
      setConfirmText("");
    } catch (err) {
      console.error("Remove from school failed:", err);
      setRemoveError("Couldn't remove this user. Please try again.");
    } finally {
      setRemoving(false);
    }
  };

  // Directory import (from the shared BulkImport). Creates any new homeroom classes
  // (owned by the assigned teacher — rules allow a same-school admin to provision a
  // class), then adds/updates shared students by studentId, stamping homeroomClassId
  // + enrolledClasses for the matched/created class. Returns the count processed.
  const handleDirectoryImport = async ({ students, newClasses = [] }) => {
    if (!students?.length || !schoolId) return 0;
    const currentYear = new Date().getFullYear();

    // name(lower) → classId, seeded with existing classes.
    const nameToId = {};
    schoolClasses.forEach(c => { nameToId[(c.className || "").trim().toLowerCase()] = c.id; });

    // Create the new classes, assigned to the chosen teacher.
    const created = [];
    for (const nc of newClasses) {
      const ref = await addDoc(collection(firestore, "classes"), {
        className: nc.name,
        teacherId: nc.teacherId || uid,
        schoolId,
        isHomeroom: true,
        gradeLevel: nc.gradeLevel || "",
        schoolYear: `${currentYear}-${currentYear + 1}`,
        enrolledStudents: [],
      });
      nameToId[nc.name.trim().toLowerCase()] = ref.id;
      created.push({ id: ref.id, className: nc.name });
    }
    if (created.length) setSchoolClasses(prev => [...prev, ...created]);

    // Dedupe existing roster by studentId.
    const rosterSnap = await getDocs(
      query(collection(firestore, "students"), where("schoolId", "==", schoolId))
    );
    const byId = {};
    rosterSnap.docs.forEach(d => {
      const data = d.data();
      if (data.studentId) byId[data.studentId] = { docId: d.id, ...data };
    });

    let count = 0;
    for (const s of students) {
      const hrId = s.homeroomKey ? nameToId[s.homeroomKey] : null;
      const hr = hrId ? { homeroomClassId: hrId, enrolledClasses: arrayUnion(hrId) } : {};
      const existing = byId[s.id];
      if (existing) {
        await updateDoc(doc(firestore, "students", existing.docId), { shared: true, ...hr });
      } else {
        await addDoc(collection(firestore, "students"), {
          firstName: s.firstName,
          lastName: s.lastName,
          studentName: s.studentName,
          studentId: s.id,
          ...gradeFields(s.grade),
          schoolId,
          shared: true,
          addedBy: uid,
          enrolledClasses: hrId ? [hrId] : [],
          ...(hrId ? { homeroomClassId: hrId } : {}),
        });
      }
      count++;
    }
    return count;
  };

  if (loading || fetching) return <div>Loading…</div>;

  // Staff the admin can assign as the owner of a newly-created homeroom class.
  const teacherOptions = users.map(u => ({
    uid: u.id,
    email: u.email || "",
    name: u.displayName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || "User",
  }));

  return (
    <div className="add-student-form-container">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0 }}>Manage School Users</h1>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={handleExportRoster}
            disabled={exporting}
            title="Download the school roster as a CSV spreadsheet"
            style={{ whiteSpace: "nowrap", background: "#fff", color: "var(--color-primary)", border: "1px solid var(--color-primary)" }}
          >
            {exporting ? "Exporting…" : "⬇ Export roster (CSV)"}
          </button>
          <button onClick={() => setShowAddUser(true)} style={{ whiteSpace: "nowrap" }}>+ Add a user</button>
        </div>
      </div>
      <p style={{ color: "var(--color-secondary)", margin: "8px 0 24px" }}>
        Promote teachers to Admin or Security. Admins can manage users and see the school-wide sign-out board. Security can view the sign-out board only.
      </p>

      {/* School at a glance */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
        {[
          { label: "Staff", value: users.length },
          { label: "Admins", value: users.filter(u => (u.role || "teacher") === "admin").length },
          { label: "Active classes", value: summary.classes },
          { label: "Directory students", value: summary.students },
          { label: "Currently out", value: summary.out },
        ].map(card => (
          <div key={card.label} style={{ flex: "1 1 130px", minWidth: 130, padding: "16px 18px", background: "var(--color-white, #fff)", border: "1px solid #e5e7eb", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 13, color: "var(--color-secondary)", marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{card.value}</div>
          </div>
        ))}
      </div>

      {joinReqs.length > 0 && (
        <div style={{ marginBottom: 28, padding: "16px 18px", background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 12 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 18 }}>
            Pending join requests
            <span style={{ marginLeft: 8, fontSize: 13, color: "var(--color-secondary)" }}>{joinReqs.length}</span>
          </h2>
          <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--color-secondary)" }}>
            People asking to join your school. Approving adds them automatically.
          </p>
          {joinReqs.map(r => (
            <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "8px 0", borderTop: "1px solid #f3e3b3" }}>
              <div>
                <span style={{ fontWeight: 600 }}>{r.requesterName || r.requesterEmail || "Someone"}</span>
                {r.requesterEmail && <span style={{ color: "var(--color-secondary)", fontSize: 13, marginLeft: 8 }}>{r.requesterEmail}</span>}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => decideJoin(r.id, "approved")} style={{ background: "#16a34a", color: "#fff", minWidth: 90 }}>
                  Approve
                </button>
                <button onClick={() => decideJoin(r.id, "denied")} style={{ background: "transparent", color: "#c0392b", border: "1px solid #c0392b", minWidth: 70 }}>
                  Deny
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="table-container admin-hide-on-mobile">
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Classes</th>
            <th>Role</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {users.map(u => {
            const isMe      = u.id === uid;
            const changed   = u.pendingRole !== (u.role || "teacher");
            const isSaving  = saving[u.id];
            const st        = status[u.id];

            return (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>
                  {u.displayName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—"}
                  {isMe && <span style={{ fontSize: 12, color: "var(--color-secondary)", marginLeft: 6 }}>(you)</span>}
                </td>
                <td>{u.email}</td>
                <td>
                  {(() => {
                    const c = classCounts[u.id] || { active: 0, archived: 0 };
                    return c.archived > 0 ? `${c.active} (+${c.archived} archived)` : c.active;
                  })()}
                </td>
                <td>
                  <select
                    value={u.pendingRole}
                    onChange={e => handleRoleChange(u.id, e.target.value)}
                    disabled={isMe}
                    style={{ width: "auto" }}
                  >
                    {ROLES.map(r => (
                      <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                    ))}
                  </select>
                </td>
                <td>
                  {isMe ? (
                    <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>Can't change own role</span>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button
                        onClick={() => saveRole(u.id)}
                        disabled={!changed || isSaving}
                        style={{
                          opacity: changed ? 1 : 0.4,
                          background: st === "saved" ? "green" : undefined,
                          color: st === "saved" ? "#fff" : undefined,
                          minWidth: 70,
                        }}
                      >
                        {isSaving ? "…" : st === "saved" ? "Saved!" : "Save"}
                      </button>
                      <button
                        onClick={() => { setRemoveTarget(u); setConfirmText(""); setRemoveError(null); }}
                        title="Remove from school"
                        style={{ background: "transparent", color: "#c0392b", border: "1px solid #c0392b", minWidth: 70 }}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <MobileTableCards
        items={users}
        getKey={(u) => u.id}
        getTitle={(u) => {
          const name = u.displayName || `${u.firstName || ""} ${u.lastName || ""}`.trim() || "—";
          return u.id === uid ? `${name} (you)` : name;
        }}
        getSubtitle={(u) => `${u.email || "—"} · ${ROLE_LABELS[u.role || "teacher"]}`}
        getFields={(u) => {
          const c = classCounts[u.id] || { active: 0, archived: 0 };
          return [
            ["Email", u.email],
            ["Classes", c.archived > 0 ? `${c.active} (+${c.archived} archived)` : c.active],
            ["Role", ROLE_LABELS[u.role || "teacher"]],
          ];
        }}
        renderExtra={(u) => {
          if (u.id === uid) {
            return <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>Can't change your own role</span>;
          }
          const changed = u.pendingRole !== (u.role || "teacher");
          const isSaving = saving[u.id];
          const st = status[u.id];
          return (
            <>
              <select
                value={u.pendingRole}
                onChange={(e) => handleRoleChange(u.id, e.target.value)}
                style={{ width: "auto", margin: 0 }}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
              <button
                onClick={() => saveRole(u.id)}
                disabled={!changed || isSaving}
                style={{
                  opacity: changed ? 1 : 0.4,
                  background: st === "saved" ? "green" : undefined,
                  color: st === "saved" ? "#fff" : undefined,
                  minWidth: 70,
                }}
              >
                {isSaving ? "…" : st === "saved" ? "Saved!" : "Save"}
              </button>
              <button
                onClick={() => { setRemoveTarget(u); setConfirmText(""); setRemoveError(null); }}
                title="Remove from school"
                style={{ background: "transparent", color: "#c0392b", border: "1px solid #c0392b", minWidth: 70 }}
              >
                Remove
              </button>
            </>
          );
        }}
      />

      <div style={{ marginTop: 48, paddingTop: 32, borderTop: "1px solid #e0e0e0" }}>
        <h1 style={{ marginBottom: 8 }}>School Directory — Bulk Add Students</h1>
        <p style={{ color: "var(--color-secondary)", marginBottom: 12, maxWidth: 640 }}>
          Add the whole school once so any teacher can sign these students out by ID. Map your
          columns below — add a <strong>Homeroom / Class</strong> column to set each student's
          homeroom for emergency accountability (the name must match an existing class).
        </p>
        <BulkImport
          mode="directory"
          classOptions={schoolClasses}
          teacherOptions={teacherOptions}
          defaultTeacherId={uid}
          onImport={handleDirectoryImport}
        />
      </div>

      {removeTarget && (
        <div
          onClick={() => !removing && setRemoveTarget(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 12, padding: 24, maxWidth: 460, width: "100%", boxShadow: "0 10px 40px rgba(0,0,0,0.2)" }}
          >
            <h2 style={{ marginTop: 0 }}>Remove from school</h2>
            <p style={{ color: "var(--color-secondary)" }}>
              This moves <strong>{removeLabel(removeTarget)}</strong> and their classes (plus
              their private roster students) into their own private “school of one.” They keep
              their account and data but leave this school. Shared directory students stay.
              It’s reversible — they can re-join later.
            </p>
            <p style={{ marginBottom: 6 }}>
              Type <strong>{removeLabel(removeTarget)}</strong> to confirm:
            </p>
            <input
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder={removeLabel(removeTarget)}
              autoFocus
              style={{ width: "100%", boxSizing: "border-box", marginBottom: 12 }}
            />
            {removeError && (
              <p style={{ color: "var(--color-error, #c0392b)", margin: "0 0 12px" }}>{removeError}</p>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setRemoveTarget(null); setConfirmText(""); }}
                disabled={removing}
                style={{ background: "#fff", color: "#333", border: "1px solid #ccc" }}
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveFromSchool}
                disabled={removing || confirmText.trim().toLowerCase() !== removeLabel(removeTarget).toLowerCase()}
                style={{ background: "#c0392b", color: "#fff", minWidth: 150 }}
              >
                {removing ? "Removing…" : "Remove from school"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddUser && (
        <AddUserModal
          schoolId={schoolId}
          schoolName={schoolName}
          onClose={() => setShowAddUser(false)}
          onCreated={(newUser) => setUsers(prev => [...prev, newUser])}
        />
      )}
    </div>
  );
};

export default SchoolAdmin;
